<template>
  <div class="InsuranceEntryForm">
    <user-card
      class="user-card"
      :title="title"
    />

    <BaseSelect
      :label="
        te(`${labelPrefix}.admissions-label.me`) || te(`${labelPrefix}.admissions-label.not-me`)
          ? role === 'TA'
            ? t(`${labelPrefix}.admissions-label.me`)
            : role === 'A'
              ? t(`${labelPrefix}.admissions-label.not-me`)
              : t(`${labelPrefix}.admissions-label`)
          : t(`${labelPrefix}.admissions-label`)
      "
      :id="`${id}-${labelPrefix}-admissions`"
      v-model="admissions"
      :items="admissionOptions"
    />

    <div
      v-for="(entry, i) in entryList"
      :key="i"
    >
      <MonthYearComponent
        :legend="t(`${labelPrefix}.admission-date-label`, [i ? ` ${i + 1}` : ''])"
        :id="`${id}-${labelPrefix}-${i}`"
        :month="entry.month"
        :year="entry.year"
        @update:month="entry.month = $event"
        @update:year="entry.year = $event"
      />

      <BaseText
        :label="
          te(`${labelPrefix}.reason-label.me` || `te(${labelPrefix}.reason-label.not-me`)
            ? role === 'TA'
              ? t(`${labelPrefix}.reason-label.me`)
              : role === 'A'
                ? t(`${labelPrefix}.reason-label.not-me`)
                : t(`${labelPrefix}.reason-label`)
            : t(`${labelPrefix}.reason-label`)
        "
        :id="`${id}-${labelPrefix}-reason-${i + 1}`"
        rows="4"
        :placeholder="t(`${labelPrefix}.placeholder-reason-label`)"
        v-model="entry.reason"
        :max-characters="currentMaxCharacters"
        :rules="[required]"
        @keydown="(event: KeyboardEvent) => blockCharacters(event, FORBIDDEN_CHARS)"
        @paste="handlePaste"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, reactive, watch, computed } from 'vue'
import { useRules } from '@/modules/core/composables/useRulesComposable'
import UserCard from '@/modules/health/hiring/components/UserCardForm.vue'
import BaseSelect from '@/modules/core/components/ui/BaseSelect.vue'
import BaseText from '@/modules/core/components/ui/BaseText.vue'
import MedicalSituationAdmission from '@/modules/health/hiring/models/APP/MedicalSituationAdmission'
import { useI18n } from 'vue-i18n'
import MonthYearComponent from '@/modules/core/components/formFields/MonthYearComponent.vue'
import { blockCharacters } from '@/utils/input.utils'

const MAX_CHARACTERS_REASON = 900
const SEPARATOR_SIZE = 15

const ADMISSION_NUMBERS = ['1', '2', '3', '4', '5']
const FORBIDDEN_CHARS = /[_#[\]/$]/g

const { t, te } = useI18n()
const { required } = useRules()

const props = defineProps<{
  id: string
  title: string
  labelPrefix: string
  labelSuffix: string
  role: string
  entries?: MedicalSituationAdmission[]
}>()

const emit = defineEmits(['update:modelValue'])

const entries = reactive(props.entries?.length ? props.entries : [new MedicalSituationAdmission()])
const admissions = ref(String(entries.length))

const entryList = computed(() => entries)
const currentMaxCharacters = computed(() => {
  const size = MAX_CHARACTERS_REASON - SEPARATOR_SIZE * entryList.value.length
  const charactersAvailable = size / entryList.value.length

  return Math.floor(charactersAvailable)
})
const admissionOptions = computed(() => {
  return ADMISSION_NUMBERS.map((num) => ({
    label: getFormattedNumber(Number(num)),
    value: num
  }))
})

function getFormattedNumber(num: number) {
  return t(`${props.labelPrefix}.admission-suffix`, [num], num)
}

function updateFormData() {
  const newEntryCount = parseInt(admissions.value)

  for (let i = entries.length; i < newEntryCount; i++) {
    entries.push(new MedicalSituationAdmission())
  }

  if (entries.length > newEntryCount) {
    entries.splice(newEntryCount)
  }

  emit('update:modelValue', entries)
}

function initializeEntries() {
  const newEntryCount = parseInt(admissions.value)

  for (let i = entries.length; i < newEntryCount; i++) {
    entries.push(new MedicalSituationAdmission())
  }

  if (entries.length > newEntryCount) {
    entries.splice(newEntryCount)
  }

  updateFormData()
}

function handlePaste(event: ClipboardEvent) {
  const pastedText = event.clipboardData?.getData('text')
  if (pastedText) {
    const filteredText = pastedText.replace(FORBIDDEN_CHARS, ' ')
    event.preventDefault()

    document.execCommand('insertText', false, filteredText)
  }
}

watch(entries, () => updateFormData(), { deep: true })
watch(admissions, () => initializeEntries())
</script>

<style lang="scss" scoped>
.InsuranceEntryForm {
  .user-card {
    margin-top: 5%;
    margin-bottom: 5%;
  }

  v-text-field {
    text-align: center;
  }

  :deep(.justify-center) .v-field__field {
    justify-content: center;
  }

  .date {
    border: none;

    legend {
      font-family: $font-family-primary;
      font-size: $font-size-m;
      font-style: normal;
      font-weight: $font-weight-400;
      line-height: $line-height-100p;
      color: $color-neutral-dark;
      margin-bottom: $spacing-l;
    }

    & > div {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      grid-gap: $gap-s;
    }
  }
}
</style>
